import type { LocalResourcesSnapshot } from "../types.ts";

export type LocalLlmModelStatus = "in_use" | "loaded" | "idle";

/**
 * Normalize an Ollama model identifier for loose matching.
 *
 * Ollama treats `name`, `name:latest`, and `name:tag` as distinct rows in
 * `/api/ps` and `/api/tags`. A user picking `qwen3.6` in chat config will
 * resolve to `qwen3.6:latest` server-side, so a literal `===` against
 * `qwen3.6:latest` returns "not in use" / "not loaded" — the operator
 * sees a stale "idle" pill even while the run is streaming on that exact
 * model.
 *
 * The fix is conservative: split on the FIRST `:` to extract the family
 * part (everything before the colon), and strip the default `:latest`
 * tag — but only when `:latest` IS the entire tag (e.g. `name:latest`),
 * not as a suffix of a longer tag (e.g. `name:35b-a3b:latest` is a
 * different model artefact and must NOT be collapsed).
 */
export function normalizeOllamaModelId(id: string): string {
  const trimmed = id.trim();
  if (!trimmed) return "";
  const colonAt = trimmed.indexOf(":");
  if (colonAt < 0) {
    return trimmed;
  }
  const family = trimmed.slice(0, colonAt);
  const tag = trimmed.slice(colonAt + 1);
  if (tag === "latest") {
    return family;
  }
  return trimmed;
}

function isSameOllamaModel(a: string, b: string): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  // Tolerate `name` vs `name:latest`.
  return normalizeOllamaModelId(a) === normalizeOllamaModelId(b);
}

export function resolveLocalLlmModelStatus(
  snapshot: LocalResourcesSnapshot | null,
  selectedModel: string | null,
): LocalLlmModelStatus | null {
  if (!snapshot || !selectedModel?.trim()) {
    return null;
  }
  const name = selectedModel.trim();
  const ollama = snapshot.ollama;

  const inflightOllama = snapshot.gateway.inflightRuns.some((run) => {
    if (run.provider.trim().toLowerCase() !== "ollama") {
      return false;
    }
    return isSameOllamaModel(run.modelId, name);
  });
  if (inflightOllama) {
    return "in_use";
  }

  if (ollama.loadedModels.some((m) => isSameOllamaModel(m.name, name))) {
    return "loaded";
  }

  if (ollama.installedModels.some((m) => isSameOllamaModel(m.name, name))) {
    return "idle";
  }

  return null;
}

export function labelForLocalLlmModelStatus(status: LocalLlmModelStatus): string {
  switch (status) {
    case "in_use":
      return "is in use by an active Ollama run (possibly another session)";
    case "loaded":
      return "is loaded in RAM";
    case "idle":
      return "is installed but not loaded right now";
  }
}
